import { isAuthenticationError } from 'utils/httpError';
import { uuidv4 } from 'utils/helpers';
import { emitAuthTelemetry, sanitizeAuthEndpoint } from './authTelemetry';
import sessionCoordinator from './sessionCoordinator';
import { waitForAuthenticatedRequestsToResume } from './authenticatedRequestGate';
import { AUTH_SESSION_STATUS, AuthGenerationRecord, AuthSessionStatus } from './sessionTypes';

/**
 * Unified authenticated-request recovery contract.
 *
 * `apiUtils.request` remains the low-level transport. Every authenticated
 * frontend HTTP surface must send through `sendAuthenticatedRequest`.
 *
 * Audited call sites (2026-08-28):
 * - Authenticated: Redux API middleware, `apiRequestHandler`,
 *   `src/api/jobs.ts` (`getJobHtmlReport`), `src/api/agent.ts` (`streamAgentMessage`),
 *   `src/api/userPreferences.ts` (token identity only; HTTP goes through `apiRequestHandler`).
 * - Intentionally unauthenticated: none for `apiUtils.request`.
 *   `resolveApiHost` / `isNotPresignedUrl` are not HTTP callers.
 */
export interface AuthenticatedRequestContext {
  attemptId: string;
  generationId: string | null;
  accessToken: string | null;
  authenticationRecoveryAttempted: boolean;
}

export interface AuthenticatedRequestCoordinator {
  getCurrentGeneration(): AuthGenerationRecord | null;
  getSessionStatus(): AuthSessionStatus;
  waitForResumeCoordination(): Promise<AuthGenerationRecord | null>;
  recoverAuthenticationSession(): Promise<AuthGenerationRecord | null>;
  requireReauthentication(reason: string): void;
}

export const createAuthenticatedRequestContext = (
  generation: AuthGenerationRecord | null = sessionCoordinator.getCurrentGeneration()
): AuthenticatedRequestContext => ({
  attemptId: uuidv4(),
  generationId: generation?.generationId ?? null,
  accessToken: generation?.accessToken ?? null,
  authenticationRecoveryAttempted: false,
});

export const getCurrentAccessToken = (
  coordinator: AuthenticatedRequestCoordinator = sessionCoordinator
): string | null => coordinator.getCurrentGeneration()?.accessToken ?? null;

const isSameGeneration = (left: AuthenticatedRequestContext, right: AuthGenerationRecord | null): boolean =>
  Boolean(right && left.generationId === right.generationId && left.accessToken === right.accessToken);

const replayWithGeneration = async <T>(
  execute: (accessToken: string | null) => Promise<T>,
  context: AuthenticatedRequestContext,
  generation: AuthGenerationRecord,
  coordinator: AuthenticatedRequestCoordinator,
  endpointTemplate: string | null,
  recoveryReason: 'stale_generation' | 'coordinated_renewal'
): Promise<T> => {
  context.generationId = generation.generationId;
  context.accessToken = generation.accessToken;
  try {
    const response = await execute(generation.accessToken);
    emitAuthTelemetry('auth.request.401_recovered', {
      attemptId: context.attemptId,
      generationId: generation.generationId,
      endpointTemplate,
      httpStatus: 401,
      reason: recoveryReason,
      retryCount: 1,
    });
    return response;
  } catch (replayError) {
    if (isAuthenticationError(replayError)) {
      coordinator.requireReauthentication('second_401');
    }
    throw replayError;
  }
};

export const sendAuthenticatedRequest = async <T>({
  execute,
  context,
  coordinator = sessionCoordinator,
  endpoint,
}: {
  execute: (accessToken: string | null) => Promise<T>;
  context?: AuthenticatedRequestContext;
  coordinator?: AuthenticatedRequestCoordinator;
  endpoint?: string;
}): Promise<T> => {
  await waitForAuthenticatedRequestsToResume();
  await coordinator.waitForResumeCoordination();
  const currentGeneration = coordinator.getCurrentGeneration();
  const requestContext = context ?? createAuthenticatedRequestContext(currentGeneration);
  const endpointTemplate = endpoint ? sanitizeAuthEndpoint(endpoint) : null;
  const accessToken = currentGeneration?.accessToken ?? requestContext.accessToken;
  const alreadyTerminal =
    coordinator.getSessionStatus() === AUTH_SESSION_STATUS.reauthenticationRequired ||
    coordinator.getSessionStatus() === AUTH_SESSION_STATUS.redirectingToLogin ||
    coordinator.getSessionStatus() === AUTH_SESSION_STATUS.authenticationError ||
    coordinator.getSessionStatus() === AUTH_SESSION_STATUS.loggingOut;

  if (!requestContext.authenticationRecoveryAttempted) {
    requestContext.generationId = currentGeneration?.generationId ?? requestContext.generationId;
    requestContext.accessToken = accessToken;
  }

  try {
    return await execute(accessToken);
  } catch (error) {
    const status =
      error && typeof error === 'object' ? (error as { response?: { status?: number } }).response?.status : null;
    if (status === 403) {
      emitAuthTelemetry('auth.request.403', {
        attemptId: requestContext.attemptId,
        generationId: requestContext.generationId,
        endpointTemplate,
        httpStatus: 403,
        reason: 'forbidden',
        retryCount: 0,
      });
    }
    if (!isAuthenticationError(error)) {
      throw error;
    }

    if (requestContext.authenticationRecoveryAttempted || alreadyTerminal) {
      coordinator.requireReauthentication('second_401');
      throw error;
    }

    const latestGeneration = coordinator.getCurrentGeneration();
    if (latestGeneration && !isSameGeneration(requestContext, latestGeneration)) {
      requestContext.authenticationRecoveryAttempted = true;
      return replayWithGeneration(
        execute,
        requestContext,
        latestGeneration,
        coordinator,
        endpointTemplate,
        'stale_generation'
      );
    }

    requestContext.authenticationRecoveryAttempted = true;
    const renewed = await coordinator.recoverAuthenticationSession();
    if (!renewed) {
      throw error;
    }

    return replayWithGeneration(execute, requestContext, renewed, coordinator, endpointTemplate, 'coordinated_renewal');
  }
};
